import type { Mansion } from '@/world/mansion';
import type { FloorId } from '@/world/types';
import type { DoorEnt, StairsEnt } from '@/world/builder';
import { findPath } from './nav';
import { tileOf } from './physics';

/** Closest usable stairs that make progress toward the destination floor. */
export function walkableStairsToward(m: Mansion, from: { floor: FloorId; x: number; y: number }, to: FloorId, passDoor: (d: DoorEnt) => boolean): StairsEnt | null {
  if (from.floor === to) return null;
  const hops = new Map<FloorId, number>([[to, 0]]), queue = [to];
  for (let i = 0; i < queue.length; i++) {
    const next = queue[i];
    // Follow incoming links too, so a broken asymmetric definition cannot cause
    // the route to loop between floors.
    for (const f of m.floors.values()) {
      if (hops.has(f.id) || !f.stairs.some(s => s.def.toFloor === next)) continue;
      hops.set(f.id, hops.get(next)! + 1); queue.push(f.id);
    }
  }
  const f = m.get(from.floor), distance = hops.get(from.floor);
  if (distance === undefined) return null;
  let best: StairsEnt | null = null, bestCost = Infinity;
  for (const s of f.stairs) {
    if ((hops.get(s.def.toFloor) ?? Infinity) >= distance) continue;
    if (!m.floors.get(s.def.toFloor)?.stairs.some(t => t.def.id === s.def.to)) continue;
    const path = findPath(f, tileOf(from.x), tileOf(from.y), s.far.x, s.far.y, (x, y) => {
      const d = f.doorAt.get(y * f.w + x); return !!d && passDoor(d);
    }, 8000);
    const end = path?.at(-1);
    if (!path || !end || end[0] !== s.far.x || end[1] !== s.far.y) continue;
    let cost = 0, px = from.x, py = from.y;
    for (const [x, y] of path) { cost += Math.hypot(x * 16 + 8 - px, y * 16 + 8 - py); px = x * 16 + 8; py = y * 16 + 8; }
    if (cost < bestCost) { best = s; bestCost = cost; }
  }
  return best;
}

/** Which stairs on `from` to take to get (eventually) to `to`. BFS over the floor graph. */
export function stairsToward(m: Mansion, from: FloorId, to: FloorId): StairsEnt | null {
  if (from === to) return null;
  const prev = new Map<FloorId, { floor: FloorId; via: StairsEnt } | null>();
  prev.set(from, null);
  const q: FloorId[] = [from];
  while (q.length) {
    const cur = q.shift()!;
    const f = m.floors.get(cur);
    if (!f) continue;
    for (const s of f.stairs) {
      const nf = s.def.toFloor;
      if (!m.floors.has(nf) || prev.has(nf)) continue;
      prev.set(nf, { floor: cur, via: s });
      if (nf === to) {
        // walk back to the first hop
        let step = nf;
        let hop = prev.get(step)!;
        while (hop && hop.floor !== from) {
          step = hop.floor;
          hop = prev.get(step)!;
        }
        return hop ? hop.via : null;
      }
      q.push(nf);
    }
  }
  return null;
}
